import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Button } from './ui/button';
import { Alert } from './ui/alert';
import { Badge } from './ui/badge';
import { useAppSettings } from '../context/AppSettingsContext';

const UserSessions = () => {
  const { formatDateTime, formatTime } = useAppSettings();
  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    todayOnly: true,
    activeOnly: false,
    startDate: '',
    endDate: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.todayOnly) {
        params.append('today_only', 'true');
      } else {
        if (filters.startDate) params.append('start_date', filters.startDate);
        if (filters.endDate) params.append('end_date', filters.endDate);
      }
      if (filters.activeOnly) params.append('active_only', 'true');

      // Load sessions
      const res1 = await fetch(`${API_URL}/users/session/list/?${params.toString()}`);
      const json1 = await res1.json();
      if (res1.ok && json1.success) {
        setSessions(json1.data.sessions || []);
      } else {
        setError(json1.error || 'Failed to load sessions');
      }

      // Load summary (only for today)
      if (filters.todayOnly) {
        const res2 = await fetch(`${API_URL}/users/session/today/summary/`);
        const json2 = await res2.json();
        if (res2.ok && json2.success) {
          setSummary(json2.data.summary || []);
        }
      } else {
        setSummary([]);
      }
    } catch (e) {
      setError('Network error loading sessions');
    } finally {
      setLoading(false);
    }
  }, [API_URL, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="w-full p-6">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>User Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="todayOnly"
                checked={filters.todayOnly}
                onChange={(e) => handleFilterChange('todayOnly', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="todayOnly" className="text-sm font-medium">
                Today Only
              </label>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="activeOnly"
                checked={filters.activeOnly}
                onChange={(e) => handleFilterChange('activeOnly', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="activeOnly" className="text-sm font-medium">
                Active Only
              </label>
            </div>

            {!filters.todayOnly && (
              <>
                <div>
                  <label className="text-sm font-medium block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>
              </>
            )}
          </div>

          <Button onClick={loadData} className="mb-4">
            Refresh
          </Button>

          {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>All Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Login</TableHead>
                      <TableHead>Logout</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.session_id} className={s.is_active ? 'bg-green-50' : ''}>
                        <TableCell className="font-medium">{s.full_name}</TableCell>
                        <TableCell>{formatDateTime(s.login_at)}</TableCell>
                        <TableCell>{s.logout_at ? formatDateTime(s.logout_at) : '-'}</TableCell>
                        <TableCell>{formatDuration(s.duration_seconds)}</TableCell>
                        <TableCell>
                          <Badge variant={s.is_active ? 'default' : 'secondary'}>
                            {s.is_active ? 'Active' : 'Ended'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sessions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          No sessions found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {filters.todayOnly && (
          <Card>
            <CardHeader>
              <CardTitle>Summary by User</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>First Login</TableHead>
                        <TableHead>Last Logout</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Active</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.map((row) => (
                        <TableRow key={row.user.id}>
                          <TableCell className="font-medium">{row.user.full_name}</TableCell>
                          <TableCell>{row.first_login ? formatTime(row.first_login) : '-'}</TableCell>
                          <TableCell>{row.last_logout ? formatTime(row.last_logout) : '-'}</TableCell>
                          <TableCell>{formatDuration(row.total_duration_seconds)}</TableCell>
                          <TableCell>
                            <Badge variant={row.active_sessions > 0 ? 'default' : 'secondary'}>
                              {row.active_sessions}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                      {summary.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                            No data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UserSessions;
